import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "../lib/constants";
import { IslandSelector } from "./IslandSelector";
import { LanguageToggle } from "./LanguageToggle";
import { SearchBox } from "./SearchBox";
import { useT } from "../lib/LangContext";
import { useIsSubscribed } from "../lib/useIsSubscribed";

// Scroll threshold in px past which the sticky header gains its subtle
// lift-shadow. 30px is far enough to be intentional (not fired by a
// single wheel tick or the address-bar shrink on mobile), close enough
// to feel responsive. The listener is passive so it never blocks
// scroll performance.
const SHADOW_THRESHOLD = 30;

export const Header = ({
  category,
  onCategoryChange,
  island,
  onIslandChange,
  lang,
  onLangChange,
  onSubscribe,
  weather = {},
}) => {
  const { t, tCategory } = useT();
  const isSubscribed = useIsSubscribed();
  const [lifted, setLifted] = useState(false);
  useEffect(() => {
    // Sync initial state (e.g. browser restored scroll position on refresh).
    const check = () => setLifted(window.scrollY > SHADOW_THRESHOLD);
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, []);
  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-shadow duration-200 ease-out ${
        lifted ? "shadow-[0_2px_8px_rgba(0,0,0,0.06)]" : "shadow-none"
      }`}
      data-testid="site-header"
    >
      <div className="border-b border-[#E5E5E5]">
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center gap-2 sm:gap-4 transition-[padding] duration-200 ease-out ${
            lifted ? "md:py-2" : "md:py-4"
          }`}
        >
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <span
              className={`font-serif-display leading-none transition-[font-size] duration-200 ease-out text-2xl sm:text-[28px] ${
                lifted ? "md:text-[22px]" : ""
              }`}
            >
              <span className="text-[#1A1A1A] font-normal">Islands</span>
              <span className="text-[#0AADA0]">Daily</span>
            </span>
          </Link>

          <nav
            className="hidden md:flex flex-1 justify-center"
            data-testid="category-nav"
          >
            <ul className="flex items-center gap-1">
              {CATEGORIES.map((c) => {
                const active = category === c;
                return (
                  <li key={c}>
                    <button
                      onClick={() => onCategoryChange(c)}
                      data-testid={`category-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`px-3 lg:px-4 py-2 rounded-full text-sm transition-colors ${
                        active
                          ? "bg-[#0AADA0] text-white"
                          : "text-[#6B7280] hover:text-[#1A1A1A]"
                      }`}
                    >
                      {tCategory(c)}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="ml-auto md:ml-0 flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <SearchBox />
            <Link
              to="/obituaries"
              className="hidden sm:inline-flex items-center px-3 py-2 rounded-full text-sm text-[#1A1A1A] hover:bg-[#FAFAFA] transition-colors whitespace-nowrap"
              data-testid="nav-obituaries-link"
            >
              {t("obituaries_nav")}
            </Link>
            <LanguageToggle lang={lang} onChange={onLangChange} />
            {!isSubscribed && (
            <button
              onClick={onSubscribe}
              className="inline-flex items-center justify-center p-2.5 sm:p-3 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white shadow-md hover:shadow-lg transition-all"
              data-testid="header-subscribe-btn"
              aria-label={t("subscribe")}
              title={t("subscribe")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="shrink-0"
                aria-hidden="true"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
              </svg>
            </button>
            )}
          </div>
        </div>

        <div className="md:hidden border-t border-[#F0F0F0] overflow-x-auto no-scrollbar">
          <ul className="flex items-center gap-1 px-4 py-2 whitespace-nowrap">
            <li className="flex-shrink-0">
              <Link
                to="/obituaries"
                className="px-3 py-1.5 rounded-full text-xs bg-[#F5F5F5] text-[#1A1A1A] hover:bg-[#EEEEEE] transition-colors inline-block"
                data-testid="nav-obituaries-link-mobile"
              >
                🕊️ {t("obituaries_nav")}
              </Link>
            </li>
            {CATEGORIES.map((c) => {
              const active = category === c;
              return (
                <li key={c} className="flex-shrink-0">
                  <button
                    onClick={() => onCategoryChange(c)}
                    data-testid={`category-tab-mobile-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                      active
                        ? "bg-[#0AADA0] text-white"
                        : "text-[#6B7280] hover:text-[#1A1A1A]"
                    }`}
                  >
                    {tCategory(c)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <IslandSelector island={island} onChange={onIslandChange} weather={weather} />
    </header>
  );
};
